import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import { api } from './client'

// ============================================
// Types
// ============================================

export type CardStatus = 'pending' | 'learning' | 'active' | 'mature' | 'failed'
export type ImageStatus = 'loading' | 'ready' | 'error'
export type ElementState = 'ready' | 'pending' | 'failed' | 'not_needed'
export type SortOption =
  | 'created_desc'
  | 'created_asc'
  | 'alpha_asc'
  | 'alpha_desc'
  | 'next_review'
  | 'interval_asc'
  | 'interval_desc'

export interface ElementStatus {
  status: ElementState
  url?: string
  text?: string
  translation?: string
  error?: string
}

export interface CardElementsStatus {
  image: ElementStatus
  audio_word: ElementStatus
  audio_sentence: ElementStatus
  definition: ElementStatus
  example: ElementStatus
  ready_for_review: boolean
  fully_ready: boolean
}

export interface Card {
  id: string
  deck_id: string
  word: string
  ipa?: string // IPA pronunciation based on user's region preference
  translations?: string // JSON string array e.g. '["hello", "hi"]'
  type?: string // Part of speech (noun, verb, etc.)
  gender?: string // Grammatical gender: m, f, mf, n, m-p, f-p (nouns only)
  image_url?: string
  image_status: ImageStatus
  image_prompt?: string
  audio_url?: string
  status: CardStatus
  next_review?: string
  interval?: number
  created_at: string
  updated_at: string
  elements?: CardElementsStatus
}

/**
 * Format part of speech type with grammatical gender for display.
 * Examples:
 * - type="noun", gender="m" → "noun (m)"
 * - type="noun", gender="mf" → "noun (m/f)"
 * - type="noun", gender="m-p" → "noun (m pl.)"
 * - type="verb", gender=undefined → "verb"
 */
export function formatTypeWithGender(
  type: string | undefined,
  gender: string | undefined
): string | null {
  if (!type) return null
  if (!gender) return type
  // Format gender for display
  let genderDisplay = gender
  if (gender === 'mf') {
    genderDisplay = 'm/f'
  } else if (gender.endsWith('-p')) {
    genderDisplay = `${gender.slice(0, -2)} pl.`
  }
  return `${type} (${genderDisplay})`
}

/**
 * Parse translations JSON string to array, return first as primary meaning
 */
export function getCardMeaning(card: Card): string | undefined {
  if (!card.translations) return undefined
  try {
    const arr = JSON.parse(card.translations) as string[]
    return arr.join(', ')
  } catch {
    return card.translations
  }
}

/**
 * Get example sentence from card elements
 */
export function getCardExample(card: Card): string | undefined {
  return card.elements?.example?.text
}

/**
 * Get definition from card elements
 */
export function getCardDefinition(card: Card): string | undefined {
  return card.elements?.definition?.text
}

export interface CardsResponse {
  cards: Card[]
  total: number
  offset: number
  limit: number
  has_more: boolean
}

export interface CardFilters {
  deck_id?: string
  status?: CardStatus
  sort?: SortOption
}

export interface UpdateCardRequest {
  image_prompt?: string
  regenerate?: boolean
}

// ============================================
// Query Keys
// ============================================

export const cardKeys = {
  all: ['cards'] as const,
  lists: () => [...cardKeys.all, 'list'] as const,
  list: (filters: CardFilters) => [...cardKeys.lists(), filters] as const,
  details: () => [...cardKeys.all, 'detail'] as const,
  detail: (id: string) => [...cardKeys.details(), id] as const,
}

// ============================================
// Hooks
// ============================================

const DEFAULT_LIMIT = 20

/**
 * Hook to fetch paginated cards with infinite scroll support
 */
export function useCards(filters: CardFilters = {}) {
  return useInfiniteQuery({
    queryKey: cardKeys.list(filters),
    queryFn: async ({ pageParam = 0 }) => {
      const params = new URLSearchParams()
      if (filters.deck_id) params.set('deck_id', filters.deck_id)
      if (filters.status) params.set('status', filters.status)
      if (filters.sort) params.set('sort', filters.sort)
      params.set('offset', String(pageParam))
      params.set('limit', String(DEFAULT_LIMIT))

      return api.get<CardsResponse>(`/api/cards?${params.toString()}`)
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.has_more) return undefined
      return lastPage.offset + lastPage.limit
    },
    initialPageParam: 0,
  })
}

export interface UseCardOptions {
  includeStatus?: boolean
}

/**
 * Hook to fetch a single card by ID
 * @param id Card ID
 * @param options.includeStatus If true, includes detailed element status with R2 checks (for card detail page)
 */
export function useCard(id: string, options: UseCardOptions = {}) {
  const { includeStatus = false } = options

  return useQuery({
    queryKey: [...cardKeys.detail(id), { includeStatus }],
    queryFn: () => {
      const params = includeStatus ? '?include_status=true' : ''
      return api.get<Card>(`/api/cards/${id}${params}`)
    },
    enabled: Boolean(id),
    staleTime: 60_000, // 1 minute
  })
}

/**
 * Hook to update a card (edit prompt or regenerate image)
 */
export function useUpdateCard() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCardRequest }) =>
      api.patch<Card, UpdateCardRequest>(`/api/cards/${id}`, data),
    onSuccess: (updatedCard) => {
      // Update the card in the cache
      queryClient.setQueryData(cardKeys.detail(updatedCard.id), updatedCard)
      // Invalidate list queries to refetch
      void queryClient.invalidateQueries({ queryKey: cardKeys.lists() })
    },
  })
}

/**
 * Hook to delete a card (soft delete)
 */
export function useDeleteCard() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/cards/${id}`),
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: cardKeys.lists() })

      // Return context for rollback
      return { deletedId: id }
    },
    onSuccess: (_data, id) => {
      // Remove from detail cache
      queryClient.removeQueries({ queryKey: cardKeys.detail(id) })
      // Invalidate list queries to refetch
      void queryClient.invalidateQueries({ queryKey: cardKeys.lists() })
    },
  })
}

/**
 * Hook to skip a card (mark as skipped)
 */
export function useSkipCard() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => api.post<void>(`/api/cards/${id}/skip`),
    onSuccess: (_data, id) => {
      // Remove from detail cache
      queryClient.removeQueries({ queryKey: cardKeys.detail(id) })
      // Invalidate list queries to refetch
      void queryClient.invalidateQueries({ queryKey: cardKeys.lists() })
    },
  })
}

// ============================================
// Media Regeneration
// ============================================

export type RegenerateJobType = 'image' | 'audio' | 'all'

export interface RegenerateCardResponse {
  job_id: number
  card_id: number
  job_type: RegenerateJobType
}

/**
 * Hook to regenerate card media (image and/or audio)
 */
export function useRegenerateCard() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ cardId, jobType = 'all' }: { cardId: string; jobType?: RegenerateJobType }) =>
      api.post<RegenerateCardResponse>(`/api/cards/${cardId}/regenerate?job_type=${jobType}`),
    onSuccess: (_data, { cardId }) => {
      // Invalidate card detail to refetch status
      void queryClient.invalidateQueries({ queryKey: cardKeys.detail(cardId) })
      // Invalidate list queries to refetch
      void queryClient.invalidateQueries({ queryKey: cardKeys.lists() })
    },
  })
}

// ============================================
// Audio Regeneration
// ============================================

export interface RegenerateSentenceAudioResponse {
  card_id: number
  sentence: string
  voice_count: number
  generated: number
  skipped: number
  deleted: number
  errors: Array<{ voice_id: string; error: string }>
}

/**
 * Hook to regenerate sentence audio for a card
 * This generates audio for all voice types
 */
export function useRegenerateSentenceAudio() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (cardId: string) =>
      api.post<RegenerateSentenceAudioResponse>(`/api/cards/${cardId}/regenerate-sentence-audio`),
    onSuccess: (_data, cardId) => {
      // Invalidate card detail to refetch with updated example
      void queryClient.invalidateQueries({ queryKey: cardKeys.detail(cardId) })
      // Invalidate review session to get updated audio URLs
      void queryClient.invalidateQueries({ queryKey: ['review'] })
    },
  })
}
